<template>
  <article
    class="curapick-tile"
    :data-sku="product.sku"
    :data-produktgruppe="product.produktgruppe"
  >
    <figure>
      <img
        :src="`${product.bildLink}/m/300x300/filters:format(webp)`"
        :alt="`Bild: ${product.name} der Marke ${product.varianten[0].produktreihe || ''}`"
        width="150"
        loading="lazy"
        @error="
          (e) => {
            e.target.src =
              'https://a.storyblok.com/f/167189/500x500/c396de7a80/pdp-placeholder-packshot.png'
            e.target.alt = 'Kein Bild verfügbar :('
          }
        "
      />
    </figure>
    <div class="headerWrapper">
      <header>
        <span class="text-s">{{ product.name }}</span>
        <h3 class="h5">{{ product.varianten[0].kategorie }}</h3>
      </header>
      <nav>
        <button
          v-if="product.varianten[0].beschreibungstext"
          class="blank"
          @click="openProductModal(product.varianten[0].beschreibungstext, product.name)"
        >
          Produktdetails
          <PflegeIcon name="info-line" size="l" />
        </button>
        <button
          v-if="product.varianten[0].anwendungstext"
          class="blank"
          @click="openProductModal(product.varianten[0].anwendungstext, 'Anwendung')"
        >
          Anwendungshinweise
          <PflegeIcon name="info-line" size="l" />
        </button>
      </nav>
    </div>
    <fieldset
      v-if="product.varianten[0].kategorie !== 'Einmalhandschuhe' && product.istGenehmigt"
      class="quantityPickerWrapper"
    >
      <div v-for="varianz in product.varianten" :key="varianz.sku">
        <label :for="`qb_${varianz.sku}`">
          <b class="text-blue">{{ varianz.mengePackung }} {{ varianz.einheit }}</b>
          <span
            v-if="
              (varianz.preisStueckBrutto || varianz.preisStueckBrutto === 0) && props.showPrices
            "
            class="text-xs"
          >
            {{ toCurrency(varianz.preisStueckBrutto) }}
          </span>
        </label>
        <PflegeQuantitybutton
          :id="`qb_${varianz.sku}`"
          v-model.number="varianz.gewaehlteAnzahl"
          :min="0"
          :max="varianz.hoechstanzahl"
          :data-sku="varianz.sku"
          readonly
        />
        <div
          v-if="varianz.gewaehlteAnzahl === varianz.hoechstanzahl || !varianz.gewaehlteAnzahl"
          class="text-s text-bold"
          v-text="
            varianz.gewaehlteAnzahl === varianz.hoechstanzahl
              ? 'Maximale Menge erreicht'
              : 'Aktuell nicht in Ihrer Box'
          "
        ></div>
      </div>
    </fieldset>
    <template v-else-if="product.istGenehmigt">
      <DisposableGlovesForm
        v-for="(gloveNumber, index) in numberOfDisposableGloves"
        :key="`glove_${gloveNumber}`"
        v-model="product"
        :default-disposable-gloves-sku="props.defaultDisposableGlovesSku"
        :glove-number
        :preselected-disposable-glove-sku="preSelectedDisposableGloves[index]?.sku"
        @delete="numberOfDisposableGloves--"
      />
      <!-- <div v-if="numberOfDisposableGloves <= 5" class="addGloveWrapper">
        <hr />
        <button class="blank" @click="numberOfDisposableGloves++">
          <PflegeIcon name="add-line" />
          Weitere Handschuhgröße hinzufügen
        </button>
      </div> -->
    </template>
  </article>
</template>

<script setup>
import { computed, ref } from 'vue'
import DisposableGlovesForm from '../DisposableGlovesForm/DisposableGlovesForm.vue'
import { toCurrency } from '../../utils/formatter'

const product = defineModel({ type: Object, required: true })
const props = defineProps({
  showPrices: {
    type: Boolean,
    required: false,
    default: true
  },
  defaultDisposableGlovesSku: {
    type: String,
    required: false,
    default: '9901384' // vinyl M
  }
})

const emit = defineEmits({
  openProductModal: (props) => {
    return !!props.content
  }
})
const openProductModal = (content, heading = null) => {
  emit('openProductModal', {
    content,
    heading
  })
}

const numberOfPickedVariances = computed(() =>
  product.value.varianten.reduce(
    (accumulator, varianz) => accumulator + (!!varianz.gewaehlteAnzahl ? 1 : 0),
    0
  )
)

/* DisposableGloves */
const numberOfDisposableGloves = ref(numberOfPickedVariances.value || 1)
const preSelectedDisposableGloves = product.value.varianten.filter(
  (varianz) => varianz.gewaehlteAnzahl > 0
)
</script>

<style>
.curapick .curapick-tile {
  display: grid;
  align-items: flex-start;
  grid-template-columns: 100px 1fr 1fr;
  background-color: var(--white);
  border-radius: 0.5rem;
  padding: 0.5rem;
  gap: 1rem;
}
.curapick .curapick-tile > * {
  grid-column: 2 / span 2;
}
@container (width > 590px) {
  .curapick .curapick-tile {
    grid-template-columns: 150px 1fr 12rem;
    height: 100%;
  }
  .curapick .curapick-tile > .quantityPickerWrapper {
    grid-column-start: 3;
  }
  .curapick .curapick-tile > :is(.quantityPickerWrapper, .headerWrapper) {
    grid-column-end: span 1;
  }
  .curapick
    .notApprovedProductsWrapper
    .curapick-tile
    > :is(.quantityPickerWrapper, .headerWrapper) {
    grid-column-end: span 2;
  }
  .curapick .curapick-tile > .isFirstGlove ~ .quantityPickerWrapper {
    grid-column: 2 / span 2;
  }
}
@container (width <= 590px) {
  .curapick .curapick-tile > .isFirstGlove ~ * {
    grid-column: 1 / span 3;
  }
}
.curapick .curapick-tile .quantityPickerWrapper label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.curapick .curapick-tile .quantityPickerWrapper .Quantitybutton {
  padding-block: 0.25rem;
}
.curapick .curapick-tile button.blank {
  appearance: none;
  background: none;
  border: 0;
  text-align: left;
  font-weight: 600;
  font-family: inherit;
  font-size: 0.8em;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5em;
  cursor: pointer;
}
.curapick .curapick-tile button.blank svg {
  flex-shrink: 0;
}
.curapick .curapick-tile fieldset {
  border: 0;
  padding: 0;
  margin: 0;
}

.curapick .curapick-tile > figure {
  margin: 0;
  grid-column: 1 / span 1;
}
.curapick .curapick-tile > figure img {
  max-width: 100%;
  border-radius: 0.5rem;
}

.curapick .curapick-tile > div header h3 {
  hyphens: auto;
  word-break: initial;
}
@container (width > 600px) {
  .curapick .curapick-tile > div header h3 {
    hyphens: inherit;
  }
}
.curapick .curapick-tile > div nav {
  padding-block-start: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.curapick
  .curapick-tile:not([data-produktgruppe='EHS'])
  > fieldset
  > div:not(:first-of-type, .text-s) {
  margin-block-start: 1rem;
}
.curapick .curapick-tile > fieldset p {
  margin: 0;
}

.curapick .curapick-tile .addGloveWrapper hr {
  border-block-start: 1px solid var(--mint);
  color: var(--mint);
  margin-block-start: 0;
}
.curapick .curapick-tile .addGloveWrapper button {
  padding: 0.25rem 1.1rem;
  gap: 0.75rem;
}
.curapick .curapick-tile .addGloveWrapper button svg {
  color: var(--white);
  background-color: var(--blue);
  border-radius: 50%;
  height: 1.1rem;
  width: 1.1rem;
  padding: 0.2rem;
}
</style>
